import React from 'react';
import { useLocation } from 'react-router';
import { useAtomValue, useAtom } from 'jotai/index';
import { Strip } from '../../Models/Article';
import { HeroStripParams, NewsHeaderStripParams, ShowStripStatus } from '../../Models/Dashboard/DashNewsItem';
import { AccountInfoAtom, LoginStatusAtom } from '../../store/AccountStore';
import { UpcomingReleasesAtom } from '../../store/DiscoverStore';
import { FetchReadingProgressAtom } from '../../store/ReadingProgressStore';
import { NewsHeaderStrip } from './NewsHeaderStrip';
import { HtmlContentBlockStrip } from './HtmlContentBlockStrip';
import { ReaderStrip } from './ReaderStrip';
import { WheelStrip } from './WheelStrip';
import { UpcomingReleasesWheelStrip } from './UpcomingReleasesWheelStrip';
import { ProductOrSeriesGridStrip } from './ProductSeriesGridStrip';
import { BrowseGenreListStrip } from './BrowseGenreListStrip';
import { ChapterWheelStrip } from './ChapterWheelStrip';
import { SpotlightSeriesStrip } from './SpotlightSeriesStrip';
import { NewsWheelStrip } from './NewsWheelStrip';
import { ContinueReadingGridStrip } from './ContinueReadingGridStrip';
import { HeroStrip } from './HeroStrip';
import { ReaderBenefitsStrip } from './ReaderBenefitsStrip';
import GroupedItemCollectionStrip from './GroupedItemCollectionStrip';
import ContactUsStrip from './ContactUsStrip';
import NewsLetterSignUpStrip from './NewsLetterSignUpStrip';
import SupportCenterPageLinksTemp from './SupportCenterPageLinksTemp';
import { SpacerStrip } from './SpacerStrip';
import './ArticleContent.scss';
import InkyErrorBoundary from '../../Devon/Components/InkyErrorBoundary';

interface KodanshaArticleContentProps {
    id: number;
    style: string;
    strips: Strip[];
}

export function ArticleContent({ id, style, strips }: KodanshaArticleContentProps) {
    useHashLinkScroll(id);
    let Component: React.ComponentType<KodanshaArticleContentProps> = Content;
    if (strips.some(strip => strip.container === 'upcoming-releases-wheel')) {
        Component = withUpcomingReleases(Component);
    }
    if (strips.some(strip => strip.container === 'continue-reading-grid')) {
        Component = withContinueReading(Component);
    }
    return <Component key={id} id={id} style={style} strips={strips} />;
}

function useHashLinkScroll(id: number) {
    const { hash } = useLocation();
    const lastRenderedId = React.useRef(-1);
    React.useEffect(() => {
        if (lastRenderedId.current === id) {
            return;
        }
        lastRenderedId.current = id;
        if (hash === '') {
            return;
        }
        window.requestAnimationFrame(() => {
            const scrollTarget = document.querySelector(hash);
            if (scrollTarget) {
                scrollTarget.scrollIntoView();
            }
        });
    }, [id, hash]);
}

function withUpcomingReleases(Component: React.ComponentType<KodanshaArticleContentProps>) {
    return function ArticleContentWithUpcomingReleases({ id, style, strips }: KodanshaArticleContentProps) {
        const items = useAtomValue(UpcomingReleasesAtom);
        return (
            <Component
                id={id}
                style={style}
                strips={items.length === 0
                    ? strips.filter(strip => strip.container !== 'upcoming-releases-wheel')
                    : strips}
            />
        );
    };
}

function withContinueReading(Component: React.ComponentType<KodanshaArticleContentProps>) {
    return function ArticleContentWithContinueReading({ id, style, strips }: KodanshaArticleContentProps) {
        const [items, fetchItems] = useAtom(FetchReadingProgressAtom);
        React.useEffect(() => {
            fetchItems();
        }, [fetchItems]);
        return (
            <Component
                id={id}
                style={style}
                strips={items.length === 0
                    ? strips.filter(strip => strip.container !== 'continue-reading-grid')
                    : strips}
            />
        );
    };
}

function Content({ id, style, strips }: KodanshaArticleContentProps) {
    const loginStatus = useAtomValue(LoginStatusAtom);
    const accountInfo = useAtomValue(AccountInfoAtom);

    const stripsWithSpacers = React.useMemo(() => {
        const visibleStrips = strips.filter(strip => {
            if (strip.showStripStatus === ShowStripStatus.LoggedOut && loginStatus === 'LoggedIn') {
                return false;
            }
            if (strip.showStripStatus === ShowStripStatus.LoggedIn && loginStatus !== 'LoggedIn') {
                return false;
            }
            if (strip.showStripStatus === ShowStripStatus.LoggedInVerified && (loginStatus !== 'LoggedIn' || !accountInfo.verified)) {
                return false;
            }
            if (strip.showStripStatus === ShowStripStatus.LoggedInUnverified && (loginStatus !== 'LoggedIn' || accountInfo.verified)) {
                return false;
            }
            if ('params' in strip && 'items' in strip.params && (strip.params.items as unknown[]).length === 0) {
                return false;
            }
            return true;
        });
        const visibleStripsWithSpacers: Strip[] = [];
        for (let i = 0; i < visibleStrips.length; i++) {
            // add a spacer between two non-spacer strips
            if (i > 0 && visibleStrips[i].container !== 'spacer' && visibleStrips[i - 1].container !== 'spacer') {
                visibleStripsWithSpacers.push({ container: 'spacer', params: { height: 70 } });
            }
            visibleStripsWithSpacers.push(visibleStrips[i]);
        }
        return visibleStripsWithSpacers;
    }, [strips, loginStatus, accountInfo]);

    React.useEffect(() => {
        if (style === '') {
            return;
        }

        const styleId = `article-style-${id}`;
        let styleEl = document.getElementById(styleId);
        if (styleEl) {
            styleEl.remove();
        }
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.innerHTML = style;
        document.head.appendChild(styleEl);

        return () => {
            const styleEl = document.getElementById(styleId);
            if (styleEl) {
                styleEl.remove();
            }
        };
    }, [id, style]);

    return (
        <div className='ArticleContent'>
            {stripsWithSpacers.map((strip: Strip, i: number) => (
                <InkyErrorBoundary
                    key={i}
                    customErrorHeader={`Error rendering strip ${strip.container}`}
                    extraInfo={JSON.stringify(strip, null, 2)}
                >
                    {renderStrip(strip, i)}
                </InkyErrorBoundary>
            ))}
        </div>
    );
}

function renderStrip(strip: Strip, i: number) {
    const classNames = ['ArticleStrip'];
    if (strip.cssClass) {
        classNames.push(strip.cssClass);
    }
    let content: React.ReactElement | null;
    switch (strip.container) {
        case 'news-header':
            content = (strip.params as NewsHeaderStripParams | HeroStripParams).isHeroBanner
                ? <HeroStrip strip={strip} />
                : <NewsHeaderStrip strip={strip} />;
            break;
        case 'html-content-block':
            content = <HtmlContentBlockStrip strip={strip} />;
            break;
        case 'reader':
            content = <ReaderStrip strip={strip} />;
            break;
        case 'product-series-wheel':
            content = <WheelStrip strip={strip} />;
            break;
        case 'product-series-grid':
            content = <ProductOrSeriesGridStrip strip={strip} />;
            break;
        case 'upcoming-releases-wheel':
            content = <UpcomingReleasesWheelStrip />;
            break;
        case 'browse-genre-list':
            content = <BrowseGenreListStrip strip={strip} />;
            break;
        case 'chapter-wheel':
            content = <ChapterWheelStrip strip={strip} />;
            break;
        case 'spotlight-series':
            content = <SpotlightSeriesStrip strip={strip} />;
            break;
        case 'news-wheel':
            content = <NewsWheelStrip />;
            break;
        case 'continue-reading-grid':
            content = <ContinueReadingGridStrip strip={strip} />;
            break;
        case 'reader-benefits':
            content = <ReaderBenefitsStrip />;
            break;
        case 'grouped-item-collection':
            content = <GroupedItemCollectionStrip strip={strip} />;
            break;
        case 'contact-form':
            content = <ContactUsStrip />;
            break;
        case 'newsletter-signup':
            content = <NewsLetterSignUpStrip/>;
            break;
        case 'support-center-page-links':
            content = <SupportCenterPageLinksTemp/>;
            break;
        case 'spacer':
            content = <SpacerStrip strip={strip} />;
            break;
        default:
            content = null;
            break;
    }
    return <div key={i} className={classNames.join(' ')}>{content}</div>;
}
